<script setup lang="ts">
import { ElMessage } from "element-plus";
import { computed, onMounted, ref } from "vue";
import { EntityBasicDetailsDto, WorkflowDefinition, WorkflowDto } from "~/api";
import { apiClient } from "~/utils/apiClient";
import { showError } from "~/utils/handleAPIError";

const props = defineProps<{
  userGuid: string;
  organizationGuid: string;
  workflowDefinitionGuid: string;
}>();

const emit = defineEmits<{
  (e: "add:workflow", value: WorkflowDto): void;
}>();

const form = ref({
  workflowName: "",
  entityDefinitionGuid: "",
});

const entities = ref<EntityBasicDetailsDto[]>([]);

const workflowDef = ref<WorkflowDefinition>();

const dialogIsOpen = defineModel<boolean>({ required: true });

const createWorkflowForUser = async () => {
  if (!props.workflowDefinitionGuid) {
    showError("Workflow definition not found.");
    return;
  }

  if (
    !form.value.entityDefinitionGuid &&
    workflowDef.value?.entityDefinitionGuid
  ) {
    showError("Please select a user to create a workflow for");
    return;
  }

  const workflowDefinitionGuid = props.workflowDefinitionGuid;

  if (!workflowDef.value) {
    showError("Workflow definition not found.");
    return;
  }

  if (!props.userGuid) {
    showError("User not found");
    return;
  }
  if (form.value.entityDefinitionGuid) {
    await apiClient.workflowInstance
      .createWorkflowInstance(
        workflowDefinitionGuid,
        form.value.entityDefinitionGuid ?? "",
        props.organizationGuid,
        form.value.workflowName,
      )
      .then((res) => {
        ElMessage.success("Workflow created successfully");
        dialogIsOpen.value = false;
        emit("add:workflow", res);
      })
      .catch(showError);
  } else {
    await apiClient.workflowInstance
      .createWorkflowInstanceWithoutEntityGuid(
        workflowDefinitionGuid,
        props.organizationGuid,
        form.value.workflowName,
      )
      .then((res) => {
        ElMessage.success("Workflow created successfully");
        dialogIsOpen.value = false;
        emit("add:workflow", res);
      })
      .catch(showError);
  }
};

const getEntities = async (entityDefinitionGuid: string) => {
  if (workflowDef.value?.entityDefinitionGuid) {
    entities.value = await apiClient.entity.getBasicEntityDetails(
      entityDefinitionGuid,
      props.organizationGuid,
    );
  }
};

const getWorkflowDefinition = async (guid: string) => {
  workflowDef.value = await apiClient.workflowDefinition.getWorkflowDefinition(
    guid,
    props.organizationGuid,
  );
  getEntities(workflowDef.value.entityDefinitionGuid ?? "");
};

const handleCloseDialog = () => {
  dialogIsOpen.value = false;
};

const formReady = computed(() =>
  Boolean(
    props.workflowDefinitionGuid &&
      // form.value.workflowName &&
      (form.value.entityDefinitionGuid ||
        workflowDef.value?.entityDefinitionGuid),
  ),
);

onMounted(() => {
  getWorkflowDefinition(props.workflowDefinitionGuid);
});
</script>
<template>
  <el-dialog
    v-model="dialogIsOpen"
    :width="600"
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    :before-close="handleCloseDialog"
  >
    <template #header>Create a Workflow</template>
    <div class="WorkflowInstanceCreator">
      <el-form
        v-model="form"
        class="create-workflow-inputs"
        label-width="auto"
        label-position="left"
      >
        <el-form-item
          v-if="
            props.workflowDefinitionGuid && workflowDef?.entityDefinitionGuid
          "
          label="For"
        >
          <el-select
            v-model="form.entityDefinitionGuid"
            placeholder="Select an entity"
            clearable
            filterable
          >
            <el-option
              v-for="entity in entities"
              :key="entity.guid"
              :label="entity.name"
              :value="entity.guid"
            ></el-option>
          </el-select>
        </el-form-item>
        <!-- <el-form-item
          label="Name for Workflow"
          style="font-size: 14px; font-family: poppins, sans-serif"
        >
          <el-input
            v-model="form.workflowName"
            placeholder="Workflow Name"
          ></el-input>
        </el-form-item> -->
        <el-form-item class="create-button">
          <el-button
            type="primary"
            :disabled="
              workflowDef?.entityDefinitionGuid
                ? !formReady
                : !props.workflowDefinitionGuid
            "
            @click="createWorkflowForUser"
            >Create</el-button
          >
        </el-form-item>
      </el-form>
    </div>
  </el-dialog>
</template>
<style lang="less">
.WorkflowInstanceCreator {
  .create-button {
    margin-top: 1em;
    .el-form-item__content {
      justify-content: end;
    }
  }
  .el-input__inner {
    font-size: 14px;
    font-family: poppins, sans-serif;
  }
}
</style>
